import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminCustomers, type AdminCustomerStatus } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";
import { formatDateFR } from "@/components/admin/adminFormat";

const STATUS_LABELS: Record<AdminCustomerStatus, string> = {
  ACTIVE: "Actif",
  BLOCKED: "Bloqué",
  GUEST: "Invité",
};

const STATUS_BADGE_CLASSES: Record<AdminCustomerStatus, string> = {
  ACTIVE: "bg-green-100 text-green-800",
  BLOCKED: "bg-red-100 text-red-800",
  GUEST: "bg-cream2 text-muted",
};

const PAGE_SIZE = 20;

function isCustomerStatus(value: string): value is AdminCustomerStatus {
  return value === "ACTIVE" || value === "BLOCKED" || value === "GUEST";
}

/**
 * `/admin/clients` — équivalent de apps/web/app/(admin)/admin/clients/page.tsx :
 * liste/recherche des clients boutique (`Customer`). Filtrage par `search`
 * (téléphone/email/nom) et `status`, pagination par page — tout piloté par
 * les paramètres d'URL (`useSearchParams`, port du formulaire GET natif).
 */
export function AdminClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search")?.trim() || undefined;
  const statusParam = searchParams.get("status") ?? "";
  const status = isCustomerStatus(statusParam) ? statusParam : undefined;
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const { data: result, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "customers", { search, status, page, pageSize: PAGE_SIZE }],
    queryFn: () => getAdminCustomers(undefined, { search, status, page, pageSize: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    const nextSearch = String(formData.get("search") ?? "").trim();
    const nextStatus = String(formData.get("status") ?? "").trim();
    if (nextSearch) next.set("search", nextSearch);
    if (nextStatus) next.set("status", nextStatus);
    setSearchParams(next);
  }

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Clients</h1>
      <p className="mt-1 text-sm text-muted">{result ? `${result.totalItems} client(s) au total.` : "…"}</p>

      {isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(error)}
        </p>
      )}

      <form onSubmit={handleFilterSubmit} className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="search" className="text-xs font-medium text-muted">
            Recherche
          </label>
          <input
            id="search"
            name="search"
            type="text"
            defaultValue={search ?? ""}
            placeholder="Téléphone, email, nom..."
            className="w-64 rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="status" className="text-xs font-medium text-muted">
            Statut
          </label>
          <select
            id="status"
            name="status"
            defaultValue={status ?? ""}
            className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
          >
            <option value="">Tous</option>
            <option value="ACTIVE">Actif</option>
            <option value="BLOCKED">Bloqué</option>
            <option value="GUEST">Invité</option>
          </select>
        </div>

        <button type="submit" className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90">
          Filtrer
        </button>
      </form>

      <div className="mt-6 overflow-x-auto rounded-sm border border-line bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-cream text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Commandes</th>
              <th className="px-4 py-3">Valeur vie client</th>
              <th className="px-4 py-3">Dernière commande</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                  Chargement…
                </td>
              </tr>
            )}
            {!isLoading &&
              result?.items.map((customer) => {
                const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "—";
                return (
                  <tr key={customer.id} className="border-b border-line last:border-0 hover:bg-cream/50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/clients/${customer.id}`} className="font-medium text-navy hover:underline">
                        {name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-ink">
                      <div>{customer.phoneE164}</div>
                      <div className="text-xs text-muted">{customer.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE_CLASSES[customer.status]}`}
                      >
                        {STATUS_LABELS[customer.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink">{customer.ordersCount}</td>
                    <td className="px-4 py-3 text-ink">
                      {customer.lifetimeValue.formatted ?? `${customer.lifetimeValue.amountMinor}`}
                    </td>
                    <td className="px-4 py-3 text-ink">{formatDateFR(customer.lastOrderAt)}</td>
                  </tr>
                );
              })}
            {!isLoading && result?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                  Aucun client ne correspond à cette recherche.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted">
          <span>
            Page {result.page} / {result.totalPages}
          </span>
          <div className="flex gap-2">
            {result.page > 1 && (
              <PageLink search={search} status={status} page={result.page - 1} label="← Précédent" />
            )}
            {result.page < result.totalPages && (
              <PageLink search={search} status={status} page={result.page + 1} label="Suivant →" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PageLink({
  search,
  status,
  page,
  label,
}: {
  search?: string;
  status?: AdminCustomerStatus;
  page: number;
  label: string;
}) {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  if (status) qs.set("status", status);
  qs.set("page", String(page));

  return (
    <Link to={`/admin/clients?${qs.toString()}`} className="font-medium text-navy hover:underline">
      {label}
    </Link>
  );
}
